import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin, message, Modal, Empty, Tag } from 'antd';
import api from '../services/api';

const GOLD = '#B8892A';
const GOLD_L = '#D4A843';
const BD = '#E8E0D0';
const MUTED = '#6B5E4E';
const BG = '#FAF8F5';
const INK = '#18140C';
const GREEN = '#2A6645';
const BLUE = '#1A4F8C';
const ORANGE = '#C96B10';
const RED = '#9B2335';

function formatCNY(val) {
  if (val == null) return '¥0.00';
  return '¥' + Number(val).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function maskName(name) {
  if (!name) return '***';
  if (name.length <= 1) return name + '**';
  return name[0] + '*'.repeat(Math.max(name.length - 1, 2));
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

const STATUS_MAP = {
  registered: { color: BLUE, bg: 'rgba(26,79,140,0.1)' },
  first_bid:  { color: ORANGE, bg: 'rgba(201,107,16,0.1)' },
  rewarded:   { color: GREEN, bg: 'rgba(42,102,69,0.1)' },
};

export default function InviteReward() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [loading, setLoading] = useState(true);
  const [codeData, setCodeData] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [referralPage, setReferralPage] = useState(1);
  const [referralTotal, setReferralTotal] = useState(0);
  const [rewards, setRewards] = useState([]);
  const [rewardPage, setRewardPage] = useState(1);
  const [rewardTotal, setRewardTotal] = useState(0);
  const [promoModal, setPromoModal] = useState(false);
  const [promoForm, setPromoForm] = useState({ name: '', phone: '', wechat: '' });
  const [promoSubmitting, setPromoSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('referrals');

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchCode = useCallback(() => {
    return api.get('/referral/my-code').then(r => setCodeData(r.data)).catch(() => {});
  }, []);

  const fetchReferrals = useCallback((page = 1) => {
    return api.get(`/referral/my-referrals?page=${page}`).then(r => {
      setReferrals(r.data?.items || r.data?.list || []);
      setReferralTotal(r.data?.total || 0);
      setReferralPage(page);
    }).catch(() => {});
  }, []);

  const fetchRewards = useCallback((page = 1) => {
    return api.get(`/referral/my-rewards?page=${page}`).then(r => {
      setRewards(r.data?.items || r.data?.list || []);
      setRewardTotal(r.data?.total || 0);
      setRewardPage(page);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([fetchCode(), fetchReferrals(1), fetchRewards(1)])
      .finally(() => setLoading(false));
  }, [fetchCode, fetchReferrals, fetchRewards]);

  const copyText = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => message.success(t('referral.copied')));
    } else {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      message.success(t('referral.copied'));
    }
  };

  const shareLink = codeData?.code
    ? `${window.location.origin}/share?ref=${codeData.code}`
    : '';

  const handlePromoSubmit = async () => {
    if (!promoForm.name || !promoForm.phone) {
      message.warning(t('referral.fillRequired'));
      return;
    }
    setPromoSubmitting(true);
    try {
      await api.post('/referral/apply-promoter', {
        name: promoForm.name,
        contactInfo: JSON.stringify({ phone: promoForm.phone, wechat: promoForm.wechat }),
      });
      message.success(t('referral.promoApplied'));
      setPromoModal(false);
      fetchCode();
    } catch (e) {
      message.error(e?.response?.data?.message || t('referral.promoFailed'));
    } finally {
      setPromoSubmitting(false);
    }
  };

  const handleSaveImage = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const el = document.getElementById('share-card-render');
      if (!el) return;
      const canvas = await html2canvas(el, { useCORS: true, scale: 2 });
      const link = document.createElement('a');
      link.download = 'invite-card.png';
      link.href = canvas.toDataURL();
      link.click();
      message.success(t('referral.imageSaved'));
    } catch {
      copyText(shareLink);
      message.info(t('referral.linkCopiedFallback'));
    }
  };

  const stats = codeData?.stats || {};
  const isPromoter = codeData?.is_promoter;

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: 60 }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '14px 16px',
          background: '#fff', borderBottom: `1px solid ${BD}`,
        }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: INK }}>←</button>
          <span style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 16, color: INK }}>{t('referral.pageTitle')}</span>
          <div style={{ width: 28 }} />
        </div>

        {/* A) Hero Banner */}
        <div style={{
          margin: '16px 16px 0', borderRadius: 14, overflow: 'hidden',
          background: 'linear-gradient(135deg, #1A1A1A 0%, #2E2418 50%, #1A1A1A 100%)',
          padding: isMobile ? '28px 20px' : '36px 32px', position: 'relative',
        }}>
          <div style={{
            position: 'absolute', right: 20, top: 20,
            width: 70, height: 70, borderRadius: '50%',
            border: `2px solid rgba(184,137,42,0.3)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 32, opacity: 0.6,
          }}>🎁</div>
          <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, color: '#fff', fontFamily: 'var(--serif)', marginBottom: 8 }}>
            {t('referral.heroTitle')}
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, maxWidth: 400, marginBottom: 20 }}>
            {t('referral.heroSubtitle')}
          </div>

          {/* Referral Code */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{t('referral.myCode')}</span>
            <span style={{
              background: 'rgba(184,137,42,0.2)', border: `1px solid ${GOLD}`,
              borderRadius: 6, padding: '4px 14px', color: GOLD_L,
              fontFamily: 'var(--mono)', fontSize: 18, fontWeight: 700, letterSpacing: 2,
            }}>{codeData?.code || '------'}</span>
            <button onClick={() => copyText(codeData?.code || '')} style={{
              background: GOLD, color: '#fff', border: 'none', borderRadius: 6,
              padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600,
            }}>{t('referral.copy')}</button>
          </div>

          {/* Share Link */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{t('referral.shareLink')}</span>
            <span style={{
              color: 'rgba(255,255,255,0.7)', fontSize: 11, fontFamily: 'var(--mono)',
              maxWidth: isMobile ? 160 : 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{shareLink}</span>
            <button onClick={() => copyText(shareLink)} style={{
              background: 'transparent', color: GOLD_L, border: `1px solid ${GOLD}`,
              borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer',
            }}>{t('referral.copy')}</button>
          </div>
        </div>

        {/* B) Share Card Section */}
        <div style={{ margin: '16px 16px 0', background: '#fff', borderRadius: 12, border: `1px solid ${BD}`, padding: isMobile ? 16 : 24 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: INK, marginBottom: 16 }}>{t('referral.shareCardTitle')}</div>

          {/* Rendered Share Card */}
          <div id="share-card-render" style={{
            background: 'linear-gradient(145deg, #1A1A1A, #2E2418)',
            borderRadius: 12, padding: 24, textAlign: 'center',
            maxWidth: 320, margin: '0 auto',
          }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: GOLD_L, fontFamily: 'var(--serif)', marginBottom: 4 }}>
              {t('brand.name')}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 16 }}>
              {t('referral.cardTagline')}
            </div>
            {/* QR Code Placeholder */}
            <div style={{
              width: 120, height: 120, margin: '0 auto 12px',
              background: '#fff', borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 4,
            }}>
              <div style={{ fontSize: 32 }}>📱</div>
              <div style={{ fontSize: 9, color: MUTED }}>QR Code</div>
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>
              {t('referral.cardInviteCode')}
            </div>
            <div style={{
              fontSize: 20, fontWeight: 700, color: GOLD_L,
              fontFamily: 'var(--mono)', letterSpacing: 3,
            }}>{codeData?.code || '------'}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 12 }}>
              {t('referral.cardFooter')}
            </div>
          </div>

          {/* Share Buttons */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
            <button onClick={handleSaveImage} style={{
              background: GOLD, color: '#fff', border: 'none', borderRadius: 8,
              padding: '8px 20px', fontSize: 13, cursor: 'pointer', fontWeight: 600,
            }}>{t('referral.saveImage')}</button>
            <button onClick={() => copyText(shareLink)} style={{
              background: '#fff', color: GOLD, border: `1px solid ${GOLD}`, borderRadius: 8,
              padding: '8px 20px', fontSize: 13, cursor: 'pointer', fontWeight: 600,
            }}>{t('referral.copyLink')}</button>
          </div>
        </div>

        {/* C) Stats Dashboard */}
        <div style={{ margin: '16px 16px 0' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: INK, marginBottom: 12, paddingLeft: 2 }}>{t('referral.myStats')}</div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr',
            gap: 10,
          }}>
            {[
              { key: 'totalInvited', value: stats.totalReferrals ?? 0, icon: '👥', color: BLUE },
              { key: 'totalRegistered', value: stats.registeredCount ?? 0, icon: '✅', color: GREEN },
              { key: 'totalRewarded', value: stats.rewardedCount ?? 0, icon: '🏆', color: ORANGE },
              { key: 'totalEarned', value: formatCNY(stats.totalRewards), icon: '💰', color: GOLD },
            ].map(s => (
              <div key={s.key} style={{
                background: '#fff', borderRadius: 10, border: `1px solid ${BD}`,
                padding: '16px 14px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>{s.icon}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: s.color, fontFamily: 'var(--mono)' }}>
                  {s.value}
                </div>
                <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>{t(`referral.stat_${s.key}`)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tab Switch */}
        <div style={{
          margin: '20px 16px 0', display: 'flex', gap: 0,
          borderBottom: `1px solid ${BD}`,
        }}>
          {['referrals', 'rewards'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              flex: 1, background: 'none', border: 'none', cursor: 'pointer',
              padding: '10px 0', fontSize: 14, fontWeight: activeTab === tab ? 600 : 400,
              color: activeTab === tab ? GOLD : MUTED,
              borderBottom: activeTab === tab ? `2px solid ${GOLD}` : '2px solid transparent',
            }}>{t(`referral.tab_${tab}`)}</button>
          ))}
        </div>

        {/* D) My Referral List */}
        {activeTab === 'referrals' && (
          <div style={{ margin: '0 16px' }}>
            {referrals.length === 0 ? (
              <div style={{ padding: '40px 0' }}><Empty description={t('referral.noReferrals')} /></div>
            ) : (
              <>
                {referrals.map((r, i) => {
                  const st = STATUS_MAP[r.status] || STATUS_MAP.registered;
                  return (
                    <div key={r.id || i} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '14px 0', borderBottom: `1px solid ${BD}`,
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: INK }}>
                          {r.referee_name || maskName(r.name || r.phone)}
                        </div>
                        <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
                          {formatDate(r.created_at)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Tag style={{
                          background: st.bg, color: st.color, border: 'none',
                          borderRadius: 4, fontSize: 11, margin: 0,
                        }}>{t(`referral.status_${r.status}`)}</Tag>
                        {r.reward_amount != null && r.reward_amount > 0 && (
                          <span style={{ fontSize: 13, fontWeight: 600, color: GREEN, fontFamily: 'var(--mono)' }}>
                            +{formatCNY(r.reward_amount)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {/* Pagination */}
                {referralTotal > 10 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '16px 0' }}>
                    <button disabled={referralPage <= 1} onClick={() => fetchReferrals(referralPage - 1)} style={{
                      background: '#fff', border: `1px solid ${BD}`, borderRadius: 6,
                      padding: '6px 14px', fontSize: 12, cursor: referralPage <= 1 ? 'not-allowed' : 'pointer',
                      color: referralPage <= 1 ? MUTED : INK, opacity: referralPage <= 1 ? 0.5 : 1,
                    }}>{t('referral.prev')}</button>
                    <span style={{ padding: '6px 8px', fontSize: 12, color: MUTED }}>{referralPage}</span>
                    <button disabled={referrals.length < 10} onClick={() => fetchReferrals(referralPage + 1)} style={{
                      background: '#fff', border: `1px solid ${BD}`, borderRadius: 6,
                      padding: '6px 14px', fontSize: 12, cursor: referrals.length < 10 ? 'not-allowed' : 'pointer',
                      color: referrals.length < 10 ? MUTED : INK, opacity: referrals.length < 10 ? 0.5 : 1,
                    }}>{t('referral.next')}</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* F) Rewards History */}
        {activeTab === 'rewards' && (
          <div style={{ margin: '0 16px' }}>
            {rewards.length === 0 ? (
              <div style={{ padding: '40px 0' }}><Empty description={t('referral.noRewards')} /></div>
            ) : (
              <>
                {rewards.map((r, i) => (
                  <div key={r.id || i} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 0', borderBottom: `1px solid ${BD}`,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: INK }}>
                        {t(`referral.rewardType_${r.type}`, { defaultValue: r.type })}
                      </div>
                      <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
                        {formatDate(r.created_at || r.date)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: GREEN, fontFamily: 'var(--mono)' }}>
                        +{formatCNY(r.amount)}
                      </span>
                      {r.status && (
                        <Tag style={{
                          background: r.status === 'paid' ? 'rgba(42,102,69,0.1)' : 'rgba(201,107,16,0.1)',
                          color: r.status === 'paid' ? GREEN : ORANGE,
                          border: 'none', borderRadius: 4, fontSize: 11, margin: 0,
                        }}>{t(`referral.rewardStatus_${r.status}`, { defaultValue: r.status })}</Tag>
                      )}
                    </div>
                  </div>
                ))}
                {rewardTotal > 10 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '16px 0' }}>
                    <button disabled={rewardPage <= 1} onClick={() => fetchRewards(rewardPage - 1)} style={{
                      background: '#fff', border: `1px solid ${BD}`, borderRadius: 6,
                      padding: '6px 14px', fontSize: 12, cursor: rewardPage <= 1 ? 'not-allowed' : 'pointer',
                      color: rewardPage <= 1 ? MUTED : INK, opacity: rewardPage <= 1 ? 0.5 : 1,
                    }}>{t('referral.prev')}</button>
                    <span style={{ padding: '6px 8px', fontSize: 12, color: MUTED }}>{rewardPage}</span>
                    <button disabled={rewards.length < 10} onClick={() => fetchRewards(rewardPage + 1)} style={{
                      background: '#fff', border: `1px solid ${BD}`, borderRadius: 6,
                      padding: '6px 14px', fontSize: 12, cursor: rewards.length < 10 ? 'not-allowed' : 'pointer',
                      color: rewards.length < 10 ? MUTED : INK, opacity: rewards.length < 10 ? 0.5 : 1,
                    }}>{t('referral.next')}</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* E) Promoter Application */}
        {!isPromoter && (
          <div style={{
            margin: '20px 16px 0', background: '#fff', borderRadius: 12,
            border: `1px solid ${BD}`, padding: isMobile ? 20 : 28,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🚀</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: INK, fontFamily: 'var(--serif)', marginBottom: 8 }}>
              {t('referral.promoTitle')}
            </div>
            <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6, marginBottom: 16, maxWidth: 360, margin: '0 auto 16px' }}>
              {t('referral.promoDesc')}
            </div>
            <div style={{
              display: 'flex', gap: isMobile ? 10 : 20,
              justifyContent: 'center', marginBottom: 20,
              flexWrap: 'wrap',
            }}>
              {[
                { icon: '💎', key: 'promoB1' },
                { icon: '📞', key: 'promoB2' },
                { icon: '📈', key: 'promoB3' },
              ].map(b => (
                <div key={b.key} style={{
                  background: BG, borderRadius: 8, padding: '12px 16px',
                  minWidth: 100, textAlign: 'center',
                }}>
                  <div style={{ fontSize: 22, marginBottom: 4 }}>{b.icon}</div>
                  <div style={{ fontSize: 11, color: MUTED }}>{t(`referral.${b.key}`)}</div>
                </div>
              ))}
            </div>
            <button onClick={() => setPromoModal(true)} style={{
              background: `linear-gradient(135deg, ${GOLD}, ${GOLD_L})`,
              color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px 32px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>{t('referral.applyPromoter')}</button>
          </div>
        )}

        {/* Promoter badge if already a promoter */}
        {isPromoter && (
          <div style={{
            margin: '20px 16px 0', background: '#fff', borderRadius: 12,
            border: `1px solid ${BD}`, padding: 20, textAlign: 'center',
          }}>
            <Tag style={{
              background: 'rgba(42,102,69,0.1)', color: GREEN,
              border: 'none', borderRadius: 6, fontSize: 13, padding: '4px 16px',
            }}>🏅 {t('referral.promoterBadge')}</Tag>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>{t('referral.promoterNote')}</div>
          </div>
        )}
      </div>

      {/* Promoter Application Modal */}
      <Modal
        open={promoModal}
        onCancel={() => setPromoModal(false)}
        footer={null}
        title={t('referral.promoModalTitle')}
        centered
        destroyOnClose
      >
        <div style={{ padding: '8px 0' }}>
          <label style={{ display: 'block', fontSize: 13, color: INK, fontWeight: 500, marginBottom: 6 }}>
            {t('referral.promoName')} <span style={{ color: RED }}>*</span>
          </label>
          <input
            value={promoForm.name}
            onChange={e => setPromoForm(f => ({ ...f, name: e.target.value }))}
            placeholder={t('referral.promoNamePh')}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8,
              border: `1px solid ${BD}`, fontSize: 14, outline: 'none',
              boxSizing: 'border-box',
            }}
          />

          <label style={{ display: 'block', fontSize: 13, color: INK, fontWeight: 500, marginBottom: 6, marginTop: 16 }}>
            {t('referral.promoPhone')} <span style={{ color: RED }}>*</span>
          </label>
          <input
            value={promoForm.phone}
            onChange={e => setPromoForm(f => ({ ...f, phone: e.target.value }))}
            placeholder={t('referral.promoPhonePh')}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8,
              border: `1px solid ${BD}`, fontSize: 14, outline: 'none',
              boxSizing: 'border-box',
            }}
          />

          <label style={{ display: 'block', fontSize: 13, color: INK, fontWeight: 500, marginBottom: 6, marginTop: 16 }}>
            {t('referral.promoWechat')}
          </label>
          <input
            value={promoForm.wechat}
            onChange={e => setPromoForm(f => ({ ...f, wechat: e.target.value }))}
            placeholder={t('referral.promoWechatPh')}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8,
              border: `1px solid ${BD}`, fontSize: 14, outline: 'none',
              boxSizing: 'border-box',
            }}
          />

          <button
            onClick={handlePromoSubmit}
            disabled={promoSubmitting}
            style={{
              width: '100%', marginTop: 24,
              background: promoSubmitting ? MUTED : `linear-gradient(135deg, ${GOLD}, ${GOLD_L})`,
              color: '#fff', border: 'none', borderRadius: 8,
              padding: '12px 0', fontSize: 15, fontWeight: 600, cursor: promoSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            {promoSubmitting ? t('referral.submitting') : t('referral.submitApplication')}
          </button>
        </div>
      </Modal>
    </div>
  );
}
